import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ProfessionalList from '../components/ProfessionalList';
import ProfessionalForm from '../components/ProfessionalForm';
import ClientList from '../components/ClientList';
import ClientForm from '../components/ClientForm';
import ServiceList from '../components/ServiceList';
import ServiceForm from '../components/ServiceForm';
import AgendaList from '../components/AgendaList';
import AgendaForm from '../components/AgendaForm';
import SchedulingCalendar from '../components/SchedulingCalendar';
import HolidayManager from '../components/HolidayManager';
import WhatsAppConfigPage from './WhatsAppConfigPage';
import WhatsAppHelpPage from './WhatsAppHelpPage';
import CompanySettings from './CompanySettings';
import UserList from '../components/UserList';
import UserForm from '../components/UserForm';
import UserProfileModal from '../components/UserProfileModal';
import api from '../services/api';
import {
    LogOut, Users, Scissors, Calendar, UserCircle,
    LayoutDashboard, MessageCircle, Settings, UserCog, User as UserIcon, Menu
} from 'lucide-react';
import { Container, Row, Col, Nav, Button, Image, Offcanvas } from 'react-bootstrap';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import './MainLayout.css';

const MainLayout = () => {
    const { logout, user } = useContext(AuthContext)!;
    const navigate = useNavigate();
    const location = useLocation();
    const [companySettings, setCompanySettings] = useState<{ trade_name?: string, logo?: string | null }>({});
    const [currentUser, setCurrentUser] = useState<{ first_name?: string, last_name?: string, photo?: string | null, is_staff?: boolean }>({});
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { status: onboardingStatus } = useOnboardingStatus();

    const isActive = (path: string) => location.pathname.includes(path);

    const fetchCompanySettings = async () => {
        try {
            const response = await api.get('/company/settings/');
            setCompanySettings(response.data);
        } catch (error) {
            console.error('Error fetching company settings for layout:', error);
        }
    };

    const fetchCurrentUser = async () => {
        try {
            const response = await api.get('/company/me/');
            setCurrentUser(response.data);
        } catch (error) {
            console.error('Error fetching current user:', error);
        }
    };

    useEffect(() => {
        fetchCompanySettings();
        fetchCurrentUser();

        // Listen for updates from CompanySettings page
        const handleSettingsUpdate = () => fetchCompanySettings();
        window.addEventListener('companySettingsUpdated', handleSettingsUpdate);

        return () => {
            window.removeEventListener('companySettingsUpdated', handleSettingsUpdate);
        };
    }, []);

    const userDisplayName = currentUser.first_name
        ? `${currentUser.first_name} ${currentUser.last_name || ''}`
        : user?.username || 'Usuário';

    const navigateTo = (path: string) => {
        navigate(path);
        setSidebarOpen(false);
    };

    const sidebarContent = (
        <>
            <div className="mb-4 pb-3 border-bottom border-secondary border-opacity-10 d-flex flex-column align-items-center text-center">
                {companySettings.logo ? (
                    <Image
                        src={companySettings.logo}
                        alt={companySettings.trade_name || "Company Logo"}
                        fluid
                        style={{ maxHeight: '60px', objectFit: 'contain' }}
                        className="mb-2"
                    />
                ) : (
                    <h4 className="mb-1 text-primary fw-bold text-truncate w-100" title={companySettings.trade_name || "Y-AGENDA"}>
                        {companySettings.trade_name || "Y-AGENDA"}
                    </h4>
                )}
                <small className="text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>
                    {companySettings.trade_name || "Professional Dashboard"}
                </small>
            </div>

            <Nav className="flex-column flex-grow-1 gap-1">
                <Nav.Link
                    active={isActive('scheduling')}
                    onClick={() => navigateTo('/scheduling')}
                    className={`d-flex align-items-center gap-3 ${isActive('scheduling') ? 'active' : ''}`}
                >
                    <LayoutDashboard size={18} /> Agendamentos
                </Nav.Link>

                <Nav.Link
                    active={isActive('holidays')}
                    onClick={() => navigateTo('/holidays')}
                    className={`d-flex align-items-center gap-3 ${isActive('holidays') ? 'active' : ''}`}
                >
                    <Calendar size={18} /> Feriados
                </Nav.Link>

                <div className="mt-4 mb-2 text-uppercase small text-secondary fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}>Gerenciamento</div>
                <Nav.Link
                    active={isActive('professionals')}
                    onClick={() => navigateTo('/professionals')}
                    className={`d-flex align-items-center gap-3 ${isActive('professionals') ? 'active' : ''}`}
                >
                    <Users size={18} /> Profissionais
                </Nav.Link>
                <Nav.Link
                    active={isActive('clients')}
                    onClick={() => navigateTo('/clients')}
                    className={`d-flex align-items-center gap-3 ${isActive('clients') ? 'active' : ''}`}
                >
                    <UserCircle size={18} /> Clientes
                </Nav.Link>
                <Nav.Link
                    active={isActive('services')}
                    onClick={() => navigateTo('/services')}
                    className={`d-flex align-items-center gap-3 ${isActive('services') ? 'active' : ''}`}
                >
                    <Scissors size={18} /> Serviços
                </Nav.Link>

                {currentUser.is_staff && (
                    <Nav.Link
                        active={isActive('users')}
                        onClick={() => navigateTo('/users')}
                        className={`d-flex align-items-center gap-3 ${isActive('users') ? 'active' : ''}`}
                    >
                        <UserCog size={18} /> Usuários
                    </Nav.Link>
                )}

                <div className="mt-4 mb-2 text-uppercase small text-secondary fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}>Sistema</div>

                {currentUser.is_staff && (
                    <Nav.Link
                        active={isActive('settings')}
                        onClick={() => navigateTo('/settings')}
                        className={`d-flex align-items-center gap-3 ${isActive('settings') ? 'active' : ''}`}
                    >
                        <Settings size={18} /> Configurações
                    </Nav.Link>
                )}

                {currentUser.is_staff && (
                    <Nav.Link
                        active={isActive('whatsapp')}
                        onClick={() => navigateTo('/whatsapp')}
                        className={`d-flex align-items-center gap-3 ${isActive('whatsapp') ? 'active' : ''}`}
                    >
                        <MessageCircle size={18} /> WhatsApp
                    </Nav.Link>
                )}
            </Nav>

            <div className="mt-auto pt-3 border-top border-secondary border-opacity-10">
                {!onboardingStatus?.onboarding_complete && onboardingStatus !== null && (
                    <div className="alert alert-warning p-2 mb-3 text-center" style={{ fontSize: '0.8rem', borderRadius: 'var(--radius-md)' }}>
                        <p className="mb-2 fw-bold text-dark">Configuração Incompleta</p>
                        <Button
                            variant="primary"
                            size="sm"
                            className="w-100"
                            onClick={() => navigate('/onboarding')}
                        >
                            Configurar Agenda
                        </Button>
                    </div>
                )}

                <div
                    className="d-flex align-items-center gap-3 mb-3 p-2 rounded cursor-pointer user-profile-link"
                    onClick={() => { setShowProfileModal(true); setSidebarOpen(false); }}
                    style={{ cursor: 'pointer' }}
                    title="Editar Perfil"
                >
                    {currentUser.photo ? (
                        <Image
                            src={currentUser.photo}
                            roundedCircle
                            style={{ width: '36px', height: '36px', objectFit: 'cover', border: '1px solid #dee2e6' }}
                        />
                    ) : (
                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', border: '1px solid #dee2e6' }}>
                            <UserIcon size={20} className="text-secondary" />
                        </div>
                    )}
                    <div className="d-flex flex-column overflow-hidden">
                        <span className="fw-bold text-truncate small text-dark">{userDisplayName}</span>
                        <span className="text-muted text-truncate" style={{ fontSize: '0.7rem' }}>Editar Perfil</span>
                    </div>
                </div>

                <Button variant="outline-secondary" onClick={logout} className="d-flex align-items-center justify-content-center gap-2 w-100 py-2">
                    <LogOut size={18} /> Sair
                </Button>
            </div>
        </>
    );

    return (
        <Container fluid className="p-0 main-layout-container" style={{ height: '100vh', overflow: 'hidden' }}>
            {/* Mobile header */}
            <div className="mobile-header d-md-none">
                <Button
                    variant="link"
                    className="text-white p-0"
                    onClick={() => setSidebarOpen(true)}
                    aria-label="Abrir menu"
                >
                    <Menu size={24} />
                </Button>
                <span className="mobile-header-title">
                    {companySettings.trade_name || "Y-AGENDA"}
                </span>
                <div style={{ width: '24px' }} />
            </div>

            {/* Mobile offcanvas sidebar */}
            <Offcanvas
                show={sidebarOpen}
                onHide={() => setSidebarOpen(false)}
                placement="start"
                className="sidebar-offcanvas"
            >
                <Offcanvas.Header closeButton closeVariant="white" />
                <Offcanvas.Body className="d-flex flex-column p-3">
                    {sidebarContent}
                </Offcanvas.Body>
            </Offcanvas>

            <Row className="h-100 g-0">
                {/* Desktop sidebar */}
                <Col md={3} lg={2} className="bg-white border-end d-none d-md-flex flex-column p-3 sidebar-col">
                    {sidebarContent}
                </Col>

                <Col className="bg-light p-4 overflow-auto h-100 content-col main-content">
                    <Routes>
                        <Route path="/" element={<Navigate to="scheduling" replace />} />

                        <Route path="scheduling" element={<SchedulingCalendar />} />

                        <Route path="professionals" element={<ProfessionalList />} />
                        <Route path="professionals/new" element={<ProfessionalForm />} />
                        <Route path="professionals/:id" element={<ProfessionalForm />} />

                        <Route path="clients" element={<ClientList />} />
                        <Route path="clients/new" element={<ClientForm />} />
                        <Route path="clients/:id" element={<ClientForm />} />

                        <Route path="services" element={<ServiceList />} />
                        <Route path="services/new" element={<ServiceForm />} />
                        <Route path="services/:id" element={<ServiceForm />} />

                        <Route path="holidays" element={<HolidayManager />} />

                        <Route path="agendas" element={<AgendaList />} />
                        <Route path="agendas/new" element={<AgendaForm />} />
                        <Route path="agendas/:id" element={<AgendaForm />} />
                        <Route path="agendas/:id" element={<AgendaForm />} />

                        <Route path="settings" element={<CompanySettings />} />

                        {/* New User Routes */}
                        <Route path="users" element={<UserList />} />
                        <Route path="users/new" element={<UserForm />} />
                        <Route path="users/:id" element={<UserForm />} />

                        <Route path="whatsapp" element={<WhatsAppConfigPage />} />
                        <Route path="whatsapp/help" element={<WhatsAppHelpPage />} />
                    </Routes>
                </Col>
            </Row>

            <UserProfileModal
                show={showProfileModal}
                onHide={() => setShowProfileModal(false)}
                onUpdate={fetchCurrentUser}
            />
        </Container>
    );
};

export default MainLayout;

